import React from "react";
import { Chip, Box } from "@mui/material";
import { useTheme } from "@mui/system";

const AutobiaBadge = ({
  label,
  variant = "filled",
  color = "primary",
  size = "medium",
  icon,
  onDelete,
  clickable = false,
  onClick,
  disabled = false,
}) => {
  const theme = useTheme();

  const getColorStyles = () => {
    const colors = {
      primary: {
        bg: theme.palette.primary.main,
        text: "white",
        outlinedBg: "transparent",
        outlinedText: theme.palette.primary.main,
        outlinedBorder: theme.palette.primary.main,
      },
      secondary: {
        bg: theme.palette.secondary.main,
        text: "white",
        outlinedBg: "transparent",
        outlinedText: theme.palette.secondary.main,
        outlinedBorder: theme.palette.secondary.main,
      },
      success: {
        bg: theme.palette.success.main,
        text: "white",
        outlinedBg: "transparent",
        outlinedText: theme.palette.success.main,
        outlinedBorder: theme.palette.success.main,
      },
      error: {
        bg: theme.palette.error.main,
        text: "white",
        outlinedBg: "transparent",
        outlinedText: theme.palette.error.main,
        outlinedBorder: theme.palette.error.main,
      },
      warning: {
        bg: theme.palette.warning.main,
        text: "white",
        outlinedBg: "transparent",
        outlinedText: theme.palette.warning.main,
        outlinedBorder: theme.palette.warning.main,
      },
      info: {
        bg: theme.palette.info.main,
        text: "white",
        outlinedBg: "transparent",
        outlinedText: theme.palette.info.main,
        outlinedBorder: theme.palette.info.main,
      },
      gray: {
        bg: theme.palette.gray.g400,
        text: "white",
        outlinedBg: "transparent",
        outlinedText: theme.palette.gray.g600,
        outlinedBorder: theme.palette.gray.g300,
      },
    };
    return colors[color] || colors.primary;
  };

  const colorStyles = getColorStyles();

  return (
    <Chip
      label={label}
      variant={variant}
      size={size}
      icon={icon}
      onDelete={onDelete}
      onClick={clickable ? onClick : undefined}
      disabled={disabled}
      sx={{
        fontFamily: theme.typography.fontFamily,
        fontWeight: 500,
        borderRadius: "16px",
        cursor: clickable || onClick ? "pointer" : "default",
        ...(variant === "filled" && {
          backgroundColor: colorStyles.bg,
          color: colorStyles.text,
          "&:hover": {
            backgroundColor: clickable || onClick 
              ? `${colorStyles.bg}dd` 
              : colorStyles.bg,
          },
        }),
        ...(variant === "outlined" && {
          backgroundColor: colorStyles.outlinedBg,
          color: colorStyles.outlinedText,
          borderColor: colorStyles.outlinedBorder,
          "&:hover": {
            backgroundColor: clickable || onClick 
              ? `${colorStyles.outlinedText}08` 
              : colorStyles.outlinedBg,
          },
        }),
        "&.Mui-disabled": {
          backgroundColor: theme.palette.gray.g100,
          color: theme.palette.gray.g400,
          opacity: 0.7,
        },
        "& .MuiChip-deleteIcon": {
          color: variant === "filled" 
            ? colorStyles.text 
            : colorStyles.outlinedText,
          "&:hover": {
            color: variant === "filled" 
              ? colorStyles.text 
              : colorStyles.outlinedText,
            backgroundColor: variant === "filled" 
              ? `${colorStyles.text}20` 
              : `${colorStyles.outlinedText}20`,
          },
        },
        "& .MuiChip-icon": {
          color: variant === "filled" 
            ? colorStyles.text 
            : colorStyles.outlinedText,
        },
      }}
    />
  );
};

// Numeric badge for counters
export const AutobiaBadgeCounter = ({
  count,
  max = 99,
  color = "primary",
  size = "small",
  showZero = false,
}) => {
  const theme = useTheme();
  
  if (!showZero && count === 0) {
    return null;
  }

  const displayCount = count > max ? `${max}+` : count.toString();
  
  const colorStyles = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    success: theme.palette.success.main,
  };

  const dimensions = {
    small: { minWidth: "16px", height: "16px", fontSize: "0.625rem" },
    medium: { minWidth: "20px", height: "20px", fontSize: "0.75rem" },
    large: { minWidth: "24px", height: "24px", fontSize: "0.8rem" },
  };

  return (
    <Box
      sx={{
        backgroundColor: colorStyles[color] || colorStyles.primary,
        color: "white",
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontFamily: theme.typography.fontFamily,
        ...dimensions[size],
      }}
    >
      {displayCount}
    </Box>
  );
};

export default AutobiaBadge;